import { useEffect } from "react";
import { useAuthStore } from "../stores/authStore";
import { isJwtExpired } from "../lib/jwt";
import { endUserSession } from "../lib/session";

const CHECK_INTERVAL_MS = 60_000;

/**
 * End a customer session the moment its token is past `exp`, without waiting
 * for a request to be refused.
 *
 * A token that aged out while the tab was closed, backgrounded or restored
 * from the bfcache produces no event of its own: the app rehydrates, renders
 * the dashboard, and only learns the truth from whichever query happens to
 * fail first. Re-checking on every wake turns that into a redirect to the
 * login form instead of an error panel.
 */
export const useSessionExpiry = () => {
    const token = useAuthStore((state) => state.token);

    useEffect(() => {
        if (!token) return;

        // Re-read the store rather than closing over `token`: a login in
        // another tab, or a logout, can land between two checks.
        const check = () => {
            if (isJwtExpired(useAuthStore.getState().token)) endUserSession();
        };

        check();

        const interval = window.setInterval(check, CHECK_INTERVAL_MS);
        document.addEventListener("visibilitychange", check);
        window.addEventListener("focus", check);
        window.addEventListener("pageshow", check);

        return () => {
            window.clearInterval(interval);
            document.removeEventListener("visibilitychange", check);
            window.removeEventListener("focus", check);
            window.removeEventListener("pageshow", check);
        };
    }, [token]);
};

export default useSessionExpiry;
